import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X, Info, AlertTriangle, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";

interface CustomElement {
  id: string;
  element_type: "button" | "banner" | "popup" | "link" | "notification";
  title: string;
  content: string | null;
  position: string;
  action_type: string | null;
  action_value: string | null;
  style: Record<string, any>;
  is_active: boolean;
}

interface DynamicElementsProps {
  position: "dashboard" | "header" | "sidebar" | "all";
}

const DynamicElements = ({ position }: DynamicElementsProps) => {
  const [elements, setElements] = useState<CustomElement[]>([]);
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set());
  const [shownPopups, setShownPopups] = useState<Set<string>>(new Set());
  const [activePopup, setActivePopup] = useState<CustomElement | null>(null);

  useEffect(() => {
    fetchElements();

    // Subscribe to realtime updates
    const channel = supabase
      .channel("custom_elements_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "custom_elements" },
        () => {
          fetchElements();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    // Show popups that haven't been shown yet
    const popups = elements.filter(
      (el) =>
        el.element_type === "popup" &&
        (el.position === position || el.position === "all") &&
        !shownPopups.has(el.id)
    );
    
    if (popups.length > 0 && !activePopup) {
      const popup = popups[0];
      setActivePopup(popup);
      
      // Mark as shown if show_once is true
      if (popup.style?.show_once) {
        setShownPopups((prev) => new Set([...prev, popup.id]));
      }
    }
  }, [elements, position, shownPopups, activePopup]);

  const fetchElements = async () => {
    const { data, error } = await supabase
      .from("custom_elements")
      .select("*")
      .eq("is_active", true)
      .order("priority", { ascending: false });

    if (!error && data) {
      setElements(data as CustomElement[]);
    }
  };

  const handleAction = (element: CustomElement) => {
    if (element.action_type === "link" && element.action_value) {
      if (element.style?.external) {
        window.open(element.action_value, "_blank");
      } else {
        window.location.href = element.action_value;
      }
    }
  };

  const dismissBanner = (id: string) => {
    setDismissedBanners((prev) => new Set([...prev, id]));
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "warning":
        return <AlertTriangle className="h-4 w-4" />;
      case "success":
        return <CheckCircle className="h-4 w-4" />;
      case "error":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const getAlertVariant = (type: string): "default" | "destructive" => {
    return type === "error" ? "destructive" : "default";
  };

  const filteredElements = elements.filter(
    (el) => el.position === position || el.position === "all"
  );

  const buttons = filteredElements.filter((el) => el.element_type === "button");
  const banners = filteredElements.filter(
    (el) => el.element_type === "banner" && !dismissedBanners.has(el.id)
  );
  const links = filteredElements.filter((el) => el.element_type === "link");

  return (
    <>
      {/* Banners */}
      {banners.map((banner) => (
        <Alert
          key={banner.id}
          variant={getAlertVariant(banner.style?.type)}
          className="mb-4 relative"
        >
          {getAlertIcon(banner.style?.type)}
          <AlertTitle>{banner.title}</AlertTitle>
          {banner.content && <AlertDescription>{banner.content}</AlertDescription>}
          {banner.style?.dismissible !== false && (
            <button
              onClick={() => dismissBanner(banner.id)}
              className="absolute top-2 left-2 p-1 hover:bg-accent rounded"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </Alert>
      ))}

      {/* Buttons */}
      {buttons.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {buttons.map((button) => (
            <Button
              key={button.id}
              variant={button.style?.variant || "default"}
              size={button.style?.size || "default"}
              onClick={() => handleAction(button)}
            >
              {button.title}
            </Button>
          ))}
        </div>
      )}

      {/* Links */}
      {links.length > 0 && (
        <div className="flex flex-wrap gap-4 mb-4">
          {links.map((link) => (
            <a
              key={link.id}
              href={link.action_value || "#"}
              target={link.style?.external ? "_blank" : "_self"}
              rel={link.style?.external ? "noopener noreferrer" : undefined}
              className="text-primary hover:underline flex items-center gap-1"
            >
              {link.title}
              {link.style?.external && <ExternalLink className="h-3 w-3" />}
            </a>
          ))}
        </div>
      )}

      {/* Popup Dialog */}
      <Dialog open={!!activePopup} onOpenChange={() => closePopup()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{activePopup?.title}</DialogTitle>
            {activePopup?.content && (
              <DialogDescription>{activePopup.content}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => {
              if (activePopup) {
                handleAction(activePopup);
              }
              closePopup();
            }}>
              {activePopup?.style?.button_text || "حسناً"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DynamicElements;
